import { useEffect } from "react";

import type { Devo } from "~/hooks/usePlayerStore";
import { selectPreferredImageUrl } from "~/lib/imageFallback";
import { getDomainUrl } from "~/lib/utils";

type UseMediaSessionProps = {
  progress: number;
  playing: boolean;
  duration: number;
  devo?: Devo;
  setPlaying: (playing: boolean) => void;
  setPosition: (position: number) => void;
};

function getMediaSessionArtwork(thumbnailArt: string | null, domainUrl: string | undefined) {
  // https://developer.mozilla.org/en-US/docs/Web/API/MediaSession/metadata
  // supporting multiple artwork sizes and types is recommended for best device compatibility
  const artworkSizes = [96, 128, 192, 256, 512];
  const artworkTypes = ["image/png", "image/webp", "image/jpg", "image/svg", "image/jpeg"];

  // Return the same image with multiple sizes and types to improve compatibility across devices
  if (thumbnailArt) {
    const artworkArray = artworkSizes.flatMap((size) =>
      artworkTypes
        .filter((type) => thumbnailArt.endsWith(type.split("/")[1]))
        .map((type) => ({
          src: thumbnailArt,
          sizes: `${size}x${size}`,
          type,
        })),
    );
    // Add an extra entry with only src
    artworkArray.push({
      src: thumbnailArt,
      sizes: "",
      type: "",
    });
    return artworkArray;
  }

  // fallback to default artwork if no thumbnail is available
  const artworkArray = artworkSizes.map((size) => ({
    src: `${domainUrl ?? ""}/logos/logo-${size}.png`,
    sizes: `${size}x${size}`,
    type: "image/png",
  }));
  // Add an extra entry with only src
  artworkArray.push({
    src: `${domainUrl ?? ""}/logos/logo.png`,
    sizes: "",
    type: "",
  });
  return artworkArray;
}

export function useMediaSession({ devo, progress, duration, setPlaying, setPosition }: UseMediaSessionProps) {
  const domainUrl = getDomainUrl();

  const contributors =
    devo?.contributors
      ?.map((contributor) => contributor.name)
      .filter((name): name is string => name !== null)
      .join(", ") ??
    devo?.series?.[0]?.contributors
      ?.map((contributor) => contributor.name)
      .filter((name): name is string => name !== null)
      .join(", ") ??
    "";

  const thumbnailArt: string | null =
    selectPreferredImageUrl(devo?.series?.[0]?.images, ["Lock Screen"], { fallbackToFirstWithUrl: false }) ??
    devo?.thumbnail ??
    selectPreferredImageUrl(devo?.series?.[0]?.images, ["Thumbnail"]) ??
    null;

  const albumName = devo?.series?.[0]?.name ?? "";

  useEffect(() => {
    if (devo && "mediaSession" in navigator) {
      const artwork = getMediaSessionArtwork(thumbnailArt, domainUrl);

      navigator.mediaSession.metadata = new MediaMetadata({
        title: devo.title || "Sing Devotional",
        artist: contributors,
        album: albumName,
        artwork,
      });
      navigator.mediaSession.setActionHandler("play", () => setPlaying(true));
      navigator.mediaSession.setActionHandler("pause", () => setPlaying(false));
    }
  }, [devo, contributors, albumName, thumbnailArt, domainUrl, setPlaying]);
  // sync the media session position and duration with state
  useEffect(() => {
    if ("mediaSession" in navigator) {
      const durationValue = duration >= 0 && Number.isFinite(duration) ? duration : 0;
      const positionValue = progress >= 0 && Number.isFinite(progress) ? progress : 0;
      try {
        navigator.mediaSession.setPositionState({
          duration: durationValue,
          position: positionValue,
        });
      } catch (_e) {
        console.info("Failed to perform navigator.mediaSession.setPositionState()");
      }
      navigator.mediaSession.setActionHandler("seekto", ({ seekTime }) => seekTime && setPosition(seekTime));
      navigator.mediaSession.setActionHandler("seekforward", ({ seekOffset }) =>
        setPosition(progress + (seekOffset ?? 0)),
      );
      navigator.mediaSession.setActionHandler("seekbackward", ({ seekOffset }) =>
        setPosition(progress - (seekOffset ?? 0)),
      );
    }
  }, [duration, progress, setPosition]);
}
